<template>
  <div
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-slate-50 dark:bg-slate-900"
  >
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
    />

    <div class="flex flex-col flex-1 min-w-0 w-full">
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 px-4 py-6 md:px-6 lg:px-8">
        <div class="max-w-4xl mx-auto space-y-4">
          <!-- Hero header -->
          <section
            class="overflow-hidden rounded-2xl border border-primary/15 bg-white shadow-sm shadow-primary/5 dark:bg-slate-900 dark:border-primary/25 dark:shadow-none"
          >
            <div
              class="relative px-5 py-5 sm:px-6 sm:py-6 bg-gradient-to-br from-primary/12 via-primary/6 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent"
            >
              <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div class="flex items-start gap-4 min-w-0">
                  <span
                    class="flex shrink-0 items-center justify-center w-12 h-12 rounded-2xl bg-primary text-white shadow-lg shadow-primary/25"
                  >
                    <i class="mdi mdi-bell-ring-outline text-[24px]"></i>
                  </span>
                  <div class="min-w-0" :dir="textDir">
                    <h1
                      class="text-lg font-bold text-slate-900 dark:text-white text-start"
                    >
                      {{ t("notifications_title") }}
                    </h1>
                    <p
                      class="mt-1 text-sm text-slate-600 dark:text-slate-300 text-start"
                    >
                      {{ t("notifications_subtitle") }}
                    </p>
                  </div>
                </div>

                <div class="flex flex-wrap items-center gap-2 shrink-0">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-medium rounded-xl border transition-colors border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300 hover:border-primary/30 hover:text-primary disabled:opacity-50"
                    :disabled="store.pageLoading"
                    :title="t('notifications_refresh')"
                    @click="reloadPage"
                  >
                    <i
                      class="mdi text-[16px]"
                      :class="
                        store.pageRefreshing
                          ? 'mdi-loading mdi-spin text-primary'
                          : 'mdi-refresh'
                      "
                    ></i>
                    <span class="hidden sm:inline">{{ t("notifications_refresh") }}</span>
                  </button>

                  <button
                    v-if="store.pageLoaded && hasUnreadOnPage"
                    type="button"
                    class="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-semibold rounded-xl border transition-colors border-primary/25 bg-white/90 dark:bg-slate-800/90 text-primary hover:bg-primary/10 disabled:opacity-50"
                    :disabled="markingAll || store.pageLoading"
                    @click="markAllRead"
                  >
                    <i class="mdi mdi-done-all text-[16px]"></i>
                    {{ t("notifications_mark_all_read") }}
                  </button>
                </div>
              </div>

              <!-- Stats -->
              <div
                v-if="store.pageLoaded || store.unreadCount > 0"
                class="flex flex-wrap gap-3 mt-5"
              >
                <div
                  class="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/70 dark:bg-slate-800/70 border border-white/60 dark:border-slate-700/60 backdrop-blur-sm"
                >
                  <span
                    class="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/12 text-primary"
                  >
                    <i class="mdi mdi-email-mark-as-unread text-[16px]"></i>
                  </span>
                  <div :dir="textDir">
                    <p class="text-[10px] uppercase tracking-wide text-slate-500 text-start">
                      {{ t("notifications_unread_label") }}
                    </p>
                    <p class="text-sm font-bold text-slate-900 dark:text-white tabular-nums text-start">
                      {{ store.unreadCount }}
                    </p>
                  </div>
                </div>

                <div
                  v-if="store.pageLoaded"
                  class="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/70 dark:bg-slate-800/70 border border-white/60 dark:border-slate-700/60 backdrop-blur-sm"
                >
                  <span
                    class="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-500"
                  >
                    <i class="mdi mdi-format-list-bulleted text-[16px]"></i>
                  </span>
                  <div :dir="textDir">
                    <p class="text-[10px] uppercase tracking-wide text-slate-500 text-start">
                      {{ t("notifications_total_label") }}
                    </p>
                    <p class="text-sm font-bold text-slate-900 dark:text-white tabular-nums text-start">
                      {{ store.pageMeta.total }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- List card -->
          <section
            class="overflow-hidden bg-white border shadow-sm dark:bg-slate-900 border-slate-200/80 dark:border-slate-700/80 rounded-2xl"
          >
            <!-- Tabs -->
            <div
              class="flex gap-2 overflow-x-auto px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 custom-scrollbar"
              role="tablist"
              :aria-label="t('notifications_filter_label')"
            >
              <button
                v-for="tab in tabs"
                :key="tab.key"
                type="button"
                role="tab"
                :aria-selected="activeTab === tab.key"
                class="inline-flex shrink-0 items-center gap-1.5 h-9 px-3 text-xs font-medium rounded-xl border transition-all duration-200"
                :class="
                  activeTab === tab.key
                    ? 'border-primary/25 bg-primary/10 text-primary shadow-sm shadow-primary/5'
                    : 'border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-primary/20 hover:bg-primary/5'
                "
                @click="setTab(tab.key)"
              >
                <i class="mdi text-[14px]" :class="tab.icon"></i>
                {{ tab.label }}
              </button>
            </div>

            <!-- Content -->
            <div class="relative min-h-[16rem]">
              <NotificationSkeleton v-if="store.pageInitialLoading" :count="6" />

              <NotificationErrorState
                v-else-if="store.pageError"
                :t="t"
                @retry="reloadPage"
              />

              <NotificationEmptyState
                v-else-if="store.pageLoaded && !store.pageItems.length"
                :title="t('notifications_empty')"
                :subtitle="t('notifications_empty_hint')"
                :lang="lang"
                :t="t"
              />

              <div v-else>
                <NotificationListItem
                  v-for="item in store.pageItems"
                  :key="item.id"
                  :notification="item"
                  :lang="lang"
                  :t="t"
                  variant="page"
                  @select="onSelect"
                />
              </div>
            </div>

            <!-- Pagination -->
            <div
              v-if="store.pageLoaded && store.pageMeta.last_page > 1"
              class="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50"
            >
              <button
                type="button"
                class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-50 transition-colors"
                :disabled="store.pageMeta.current_page <= 1 || store.pageLoading"
                @click="goPage(store.pageMeta.current_page - 1)"
              >
                <i
                  class="mdi text-[14px]"
                  :class="lang === 'ar' ? 'mdi-chevron-right' : 'mdi-chevron-left'"
                ></i>
                {{ t("notifications_page_prev") }}
              </button>
              <span class="text-xs text-slate-500 tabular-nums">
                {{ store.pageMeta.current_page }} / {{ store.pageMeta.last_page }}
              </span>
              <button
                type="button"
                class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-50 transition-colors"
                :disabled="
                  store.pageMeta.current_page >= store.pageMeta.last_page ||
                  store.pageLoading
                "
                @click="goPage(store.pageMeta.current_page + 1)"
              >
                {{ t("notifications_page_next") }}
                <i
                  class="mdi text-[14px]"
                  :class="lang === 'ar' ? 'mdi-chevron-left' : 'mdi-chevron-right'"
                ></i>
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>

    <Toast
      :show="toast.show"
      :type="toast.type"
      :message="toast.message"
      @update:show="toast.show = $event"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import Toast from "@/components/Toast.vue";
import NotificationListItem from "@/components/notifications/NotificationListItem.vue";
import NotificationSkeleton from "@/components/notifications/NotificationSkeleton.vue";
import NotificationEmptyState from "@/components/notifications/NotificationEmptyState.vue";
import NotificationErrorState from "@/components/notifications/NotificationErrorState.vue";
import { useNotificationStore } from "@/stores/notifications";
import { usePageI18n } from "@/composables/usePageI18n";
import { useAuthShell } from "@/composables/useAuthShell";
import { isUnread } from "@/utils/notification";

const { lang, t } = usePageI18n();
const {
  isSidebarCollapsed,
  isSidebarOpenMobile,
  sidebarSearch,
  toggleSidebar,
  toast,
} = useAuthShell();

const router = useRouter();
const store = useNotificationStore();

const activeTab = ref("all");
const markingAll = ref(false);

const textDir = computed(() => (lang.value === "ar" ? "rtl" : "ltr"));

const tabs = computed(() => [
  {
    key: "all",
    label: t("notifications_tab_all"),
    icon: "mdi-view-grid-outline",
  },
  {
    key: "ticket",
    label: t("notifications_tab_tickets"),
    icon: "mdi-ticket-outline",
  },
  {
    key: "consultation",
    label: t("notifications_tab_consultations"),
    icon: "mdi-briefcase-outline",
  },
]);

const hasUnreadOnPage = computed(() =>
  store.pageItems.some((n) => isUnread(n)),
);

function tabCategory(key) {
  if (key === "all") return null;
  return key;
}

async function loadPage(page = 1, force = false) {
  await store.fetchPageList({
    category: tabCategory(activeTab.value),
    page,
    force,
  });
}

function reloadPage() {
  loadPage(store.pageMeta.current_page, true);
}

function setTab(key) {
  if (activeTab.value === key) return;
  activeTab.value = key;
  store.pageItems = [];
  store.pageLoaded = false;
  loadPage(1, true);
}

function goPage(page) {
  loadPage(page, true);
}

async function onSelect(notification) {
  await store.openNotification(notification, router);
}

async function markAllRead() {
  markingAll.value = true;
  try {
    await store.markAllRead();
    await reloadPage();
  } finally {
    markingAll.value = false;
  }
}

onMounted(() => {
  loadPage(1);
});
</script>
